import axios from 'axios';
export class HttpClient {
    constructor({ securityWorker, secure, format, ...axiosConfig } = {}) {
        this.securityData = null;
        this.setSecurityData = (data) => {
            this.securityData = data;
        };
        this.request = async ({ secure, path, type, query, format, body, ...params }) => {
            const secureParams = ((typeof secure === 'boolean' ? secure : this.secure) &&
                this.securityWorker &&
                (await this.securityWorker(this.securityData))) ||
                {};
            const requestParams = this.mergeRequestParams(params, secureParams);
            const responseFormat = format || this.format || undefined;
            if (type === 'multipart/form-data' &&
                body &&
                body !== null &&
                typeof body === 'object') {
                body = this.createFormData(body);
            }
            if (type === 'text/plain' &&
                body &&
                body !== null &&
                typeof body !== 'string') {
                body = JSON.stringify(body);
            }
            return this.instance
                .request({
                ...requestParams,
                headers: {
                    ...(requestParams.headers || {}),
                    ...(type ? { 'Content-Type': type } : {}),
                },
                params: query,
                responseType: responseFormat,
                data: body,
                url: path,
            })
                .then((response) => response.data);
        };
        this.instance = axios.create({
            ...axiosConfig,
            baseURL: axiosConfig.baseURL || '',
        });
        this.secure = secure;
        this.format = format;
        this.securityWorker = securityWorker;
    }
    mergeRequestParams(params1, params2) {
        const method = params1.method || (params2 && params2.method);
        return {
            ...this.instance.defaults,
            ...params1,
            ...(params2 || {}),
            headers: {
                ...((method &&
                    this.instance.defaults.headers[method.toLowerCase()]) ||
                    {}),
                ...(params1.headers || {}),
                ...((params2 && params2.headers) || {}),
            },
        };
    }
    stringifyFormItem(formItem) {
        if (typeof formItem === 'object' && formItem !== null) {
            return JSON.stringify(formItem);
        }
        else {
            return `${formItem}`;
        }
    }
    createFormData(input) {
        if (input instanceof FormData) {
            return input;
        }
        return Object.keys(input || {}).reduce((formData, key) => {
            const property = input[key];
            const propertyContent = property instanceof Array ? property : [property];
            for (const formItem of propertyContent) {
                const isFileType = formItem instanceof Blob || formItem instanceof File;
                formData.append(key, isFileType ? formItem : this.stringifyFormItem(formItem));
            }
            return formData;
        }, new FormData());
    }
}
/**
 * @title back-template-nest
 * @version 1.0
 * @contact
 *
 * The back-template-nest API description
 */
export class Api extends HttpClient {
    constructor() {
        super(...arguments);
        this.auth = {
            /**
             * @description 使用用户名密码登录
             *
             * @tags 认证
             * @name Login
             * @summary 用户名/密码登录
             * @request POST:/auth/login
             */
            login: (data, params = {}) => this.request({
                path: `/auth/login`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * @description 使用手机号和短信验证码登录
             *
             * @tags 认证
             * @name MobileLogin
             * @summary 手机验证码登录
             * @request POST:/auth/login/mobile
             */
            mobileLogin: (data, params = {}) => this.request({
                path: `/auth/login/mobile`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * @description 用户退出登录操作
             *
             * @tags 认证
             * @name Logout
             * @summary 退出登录
             * @request POST:/auth/logout
             */
            logout: (params = {}) => this.request({
                path: `/auth/logout`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 认证
             * @name TouchSession
             * @summary 续期当前登录会话
             * @request POST:/auth/session/touch
             */
            touchSession: (params = {}) => this.request({
                path: `/auth/session/touch`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * @description 用户首次登录时需要重置密码，设置新密码后完成账号验证
             *
             * @tags 认证
             * @name ResetPassword
             * @summary 首次登录重置密码
             * @request POST:/auth/reset-password
             */
            resetPassword: (data, params = {}) => this.request({
                path: `/auth/reset-password`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * @description 发送短信登录验证码
             *
             * @tags 认证
             * @name SendLoginCode
             * @summary 发送登录验证码
             * @request POST:/auth/login/send-code
             */
            sendLoginCode: (data, params = {}) => this.request({
                path: `/auth/login/send-code`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 认证
             * @name GetThirdPartyAuthorizeUrl
             * @summary 获取第三方登录授权地址
             * @request GET:/auth/third-party/{platform}/authorize-url
             */
            getThirdPartyAuthorizeUrl: (platform, query, params = {}) => this.request({
                path: `/auth/third-party/${platform}/authorize-url`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 认证
             * @name HandleThirdPartyCallback
             * @summary 第三方 OAuth 回调
             * @request GET:/auth/third-party/{platform}/callback
             */
            handleThirdPartyCallback: (platform, query, params = {}) => this.request({
                path: `/auth/third-party/${platform}/callback`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.users = {
            /**
             * No description
             *
             * @tags 用户
             * @name Create
             * @summary 添加用户
             * @request POST:/users
             */
            create: (data, params = {}) => this.request({
                path: `/users`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name FindAll
             * @summary 查询所有用户
             * @request GET:/users
             */
            findAll: (params = {}) => this.request({
                path: `/users`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name RemoveMany
             * @summary 批量删除用户
             * @request DELETE:/users
             */
            removeMany: (data, params = {}) => this.request({
                path: `/users`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name FindMany
             * @summary 分页、条件查询用户
             * @request GET:/users/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/users/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name FindOne
             * @summary 用户详情
             * @request GET:/users/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/users/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name Update
             * @summary 更新用户
             * @request PATCH:/users/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/users/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name Remove
             * @summary 删除用户
             * @request DELETE:/users/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/users/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name FindCurrent
             * @summary 当前用户详情
             * @request GET:/users/find/current
             */
            findCurrent: (params = {}) => this.request({
                path: `/users/find/current`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name AssignRoles
             * @summary 为用户分配角色
             * @request POST:/users/{id}/roles
             */
            assignRoles: (id, data, params = {}) => this.request({
                path: `/users/${id}/roles`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name GetAssignedRoles
             * @summary 获取用户已分配的角色列表
             * @request GET:/users/{id}/roles
             */
            getAssignedRoles: (id, params = {}) => this.request({
                path: `/users/${id}/roles`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name AssignMenus
             * @summary 为用户分配菜单
             * @request POST:/users/{id}/menus
             */
            assignMenus: (id, data, params = {}) => this.request({
                path: `/users/${id}/menus`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户
             * @name GetAssignedMenuCodes
             * @summary 获取用户已分配的菜单编码列表
             * @request GET:/users/{id}/menu-codes
             */
            getAssignedMenuCodes: (id, params = {}) => this.request({
                path: `/users/${id}/menu-codes`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.userCenter = {
            /**
             * No description
             *
             * @tags 个人中心
             * @name UpdateAvatar
             * @summary 更新头像
             * @request PATCH:/user-center/avatar
             */
            updateAvatar: (data, params = {}) => this.request({
                path: `/user-center/avatar`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name SendMobileCode
             * @summary 发送手机验证码
             * @request POST:/user-center/mobile/send-code
             */
            sendMobileCode: (data, params = {}) => this.request({
                path: `/user-center/mobile/send-code`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name UpdateMobile
             * @summary 更新手机号
             * @request PATCH:/user-center/mobile
             */
            updateMobile: (data, params = {}) => this.request({
                path: `/user-center/mobile`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name SendEmailLink
             * @summary 发送邮箱验证链接
             * @request POST:/user-center/email/send-link
             */
            sendEmailLink: (data, params = {}) => this.request({
                path: `/user-center/email/send-link`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name VerifyEmail
             * @summary 验证邮箱
             * @request POST:/user-center/email/verify
             */
            verifyEmail: (data, params = {}) => this.request({
                path: `/user-center/email/verify`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name ChangePassword
             * @summary 修改密码
             * @request PATCH:/user-center/password
             */
            changePassword: (data, params = {}) => this.request({
                path: `/user-center/password`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name SendResetPasswordCode
             * @summary 发送重置密码验证码（忘记密码）
             * @request POST:/user-center/reset-password/send-code
             */
            sendResetPasswordCode: (data, params = {}) => this.request({
                path: `/user-center/reset-password/send-code`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 个人中心
             * @name ResetPasswordByMobile
             * @summary 通过手机号重置密码（忘记密码）
             * @request POST:/user-center/reset-password
             */
            resetPasswordByMobile: (data, params = {}) => this.request({
                path: `/user-center/reset-password`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.notifications = {
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name Send
             * @summary 创建并发送通知
             * @request POST:/notifications
             */
            send: (data, params = {}) => this.request({
                path: `/notifications`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name FindMany
             * @summary 分页查询通知列表
             * @request GET:/notifications
             */
            findMany: (query, params = {}) => this.request({
                path: `/notifications`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name FindAllTemplates
             * @summary 查询所有消息模板（代码维护，前端发送通知时使用）
             * @request GET:/notifications/templates
             */
            findAllTemplates: (params = {}) => this.request({
                path: `/notifications/templates`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name PreviewTemplate
             * @summary 预览消息模板渲染效果
             * @request POST:/notifications/templates/{code}/preview
             */
            previewTemplate: (code, data, params = {}) => this.request({
                path: `/notifications/templates/${code}/preview`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name FindOne
             * @summary 查看通知详情（含渠道投递统计）
             * @request GET:/notifications/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/notifications/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name Remove
             * @summary 删除通知
             * @request DELETE:/notifications/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/notifications/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 消息通知管理
             * @name FindLogs
             * @summary 查询通知投递日志
             * @request GET:/notifications/{id}/logs
             */
            findLogs: (id, query, params = {}) => this.request({
                path: `/notifications/${id}/logs`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.captcha = {
            /**
             * No description
             *
             * @tags 图片验证码
             * @name Captcha
             * @summary 获取图片验证码
             * @request GET:/captcha
             */
            captcha: (params = {}) => this.request({
                path: `/captcha`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.roles = {
            /**
             * No description
             *
             * @tags 角色管理
             * @name Create
             * @summary 创建角色
             * @request POST:/roles
             */
            create: (data, params = {}) => this.request({
                path: `/roles`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name FindAll
             * @summary 查询所有角色
             * @request GET:/roles
             */
            findAll: (params = {}) => this.request({
                path: `/roles`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name RemoveMany
             * @summary 批量删除角色
             * @request DELETE:/roles
             */
            removeMany: (data, params = {}) => this.request({
                path: `/roles`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name FindMany
             * @summary 分页查询角色
             * @request GET:/roles/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/roles/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name FindOne
             * @summary 查询角色详情
             * @request GET:/roles/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/roles/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name Update
             * @summary 更新角色
             * @request PATCH:/roles/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/roles/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name Remove
             * @summary 删除角色
             * @request DELETE:/roles/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/roles/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name AssignMenus
             * @summary 为角色分配菜单
             * @request POST:/roles/{id}/menus
             */
            assignMenus: (id, data, params = {}) => this.request({
                path: `/roles/${id}/menus`,
                method: 'POST',
                body: data,
                type: 'application/json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name AssignScopedMenus
             * @summary 为角色分配指定范围内的菜单
             * @request PATCH:/roles/{id}/menus/scope
             */
            assignScopedMenus: (id, data, params = {}) => this.request({
                path: `/roles/${id}/menus/scope`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name GetAssignedMenuCodes
             * @summary 获取角色已分配的菜单编码列表
             * @request GET:/roles/{id}/menu-codes
             */
            getAssignedMenuCodes: (id, params = {}) => this.request({
                path: `/roles/${id}/menu-codes`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name AssignUsers
             * @summary 为角色分配用户
             * @request POST:/roles/{id}/users
             */
            assignUsers: (id, data, params = {}) => this.request({
                path: `/roles/${id}/users`,
                method: 'POST',
                body: data,
                type: 'application/json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 角色管理
             * @name GetAssignedUsers
             * @summary 获取角色已分配的用户列表
             * @request GET:/roles/{id}/users
             */
            getAssignedUsers: (id, params = {}) => this.request({
                path: `/roles/${id}/users`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.organization = {
            /**
             * No description
             *
             * @tags 组织管理
             * @name Create
             * @summary 创建组织
             * @request POST:/organization
             */
            create: (data, params = {}) => this.request({
                path: `/organization`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindAll
             * @summary 查询所有组织
             * @request GET:/organization
             */
            findAll: (params = {}) => this.request({
                path: `/organization`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name RemoveMany
             * @summary 批量删除组织
             * @request DELETE:/organization
             */
            removeMany: (data, params = {}) => this.request({
                path: `/organization`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindMany
             * @summary 分页查询组织
             * @request GET:/organization/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/organization/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindOne
             * @summary 查询组织详情
             * @request GET:/organization/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/organization/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name Update
             * @summary 更新组织
             * @request PATCH:/organization/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/organization/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name Remove
             * @summary 删除组织
             * @request DELETE:/organization/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/organization/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindWithDescendants
             * @summary 查询当前节点以及所有后代节点
             * @request GET:/organization/find/descendants
             */
            findWithDescendants: (query, params = {}) => this.request({
                path: `/organization/find/descendants`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name CreateByTreeData
             * @summary 基于树形结构创建子节点
             * @request POST:/organization/create/by/treeData
             */
            createByTreeData: (data, params = {}) => this.request({
                path: `/organization/create/by/treeData`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindOrganizationUsers
             * @summary 获取组织下的人员列表
             * @request GET:/organization/{id}/users
             */
            findOrganizationUsers: (id, params = {}) => this.request({
                path: `/organization/${id}/users`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name AssignUsers
             * @summary 分配组织人员
             * @request POST:/organization/{id}/users
             */
            assignUsers: (id, data, params = {}) => this.request({
                path: `/organization/${id}/users`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name UpdateOrganizationUser
             * @summary 更新组织人员信息
             * @request PATCH:/organization/{id}/users/{userId}
             */
            updateOrganizationUser: (id, userId, data, params = {}) => this.request({
                path: `/organization/${id}/users/${userId}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name RemoveOrganizationUser
             * @summary 移除组织人员
             * @request DELETE:/organization/{id}/users/{userId}
             */
            removeOrganizationUser: (id, userId, params = {}) => this.request({
                path: `/organization/${id}/users/${userId}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name FindAllWithUsers
             * @summary 查询所有组织（包含人员）
             * @request GET:/organization/find/all-with-users
             */
            findAllWithUsers: (params = {}) => this.request({
                path: `/organization/find/all-with-users`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name AssignMenus
             * @summary 为组织分配菜单
             * @request POST:/organization/{id}/menus
             */
            assignMenus: (id, data, params = {}) => this.request({
                path: `/organization/${id}/menus`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 组织管理
             * @name GetAssignedMenuCodes
             * @summary 获取组织已分配的菜单编码列表
             * @request GET:/organization/{id}/menu-codes
             */
            getAssignedMenuCodes: (id, params = {}) => this.request({
                path: `/organization/${id}/menu-codes`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.loginLogs = {
            /**
             * No description
             *
             * @tags 登录日志
             * @name FindUserNames
             * @summary 获取登录日志员工姓名列表
             * @request GET:/login-logs/user-names
             */
            findUserNames: (params = {}) => this.request({
                path: `/login-logs/user-names`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 登录日志
             * @name FindOne
             * @summary 登录日志详情
             * @request GET:/login-logs/detail/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/login-logs/detail/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 登录日志
             * @name FindMany
             * @summary 分页查询登录日志
             * @request GET:/login-logs
             */
            findMany: (query, params = {}) => this.request({
                path: `/login-logs`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.tenant = {
            /**
             * No description
             *
             * @tags 企业
             * @name Create
             * @summary 创建企业
             * @request POST:/tenant
             */
            create: (data, params = {}) => this.request({
                path: `/tenant`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name FindAll
             * @summary 查询所有企业
             * @request GET:/tenant
             */
            findAll: (params = {}) => this.request({
                path: `/tenant`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name RemoveMany
             * @summary 批量删除企业
             * @request DELETE:/tenant
             */
            removeMany: (data, params = {}) => this.request({
                path: `/tenant`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name FindMany
             * @summary 分页、条件查询企业
             * @request GET:/tenant/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/tenant/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name FindCurrent
             * @summary 获取当前企业信息，未登录即可使用
             * @request GET:/tenant/find/current
             */
            findCurrent: (params = {}) => this.request({
                path: `/tenant/find/current`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name FindOne
             * @summary 企业详情
             * @request GET:/tenant/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/tenant/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name Update
             * @summary 更新企业
             * @request PATCH:/tenant/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/tenant/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name Remove
             * @summary 删除企业
             * @request DELETE:/tenant/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/tenant/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name ExportMany
             * @summary 导出企业
             * @request GET:/tenant/export/many
             */
            exportMany: (query, params = {}) => this.request({
                path: `/tenant/export/many`,
                method: 'GET',
                query: query,
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name ImportMany
             * @summary 导入企业
             * @request POST:/tenant/import/many
             */
            importMany: (data, params = {}) => this.request({
                path: `/tenant/import/many`,
                method: 'POST',
                body: data,
                type: 'multipart/form-data',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name AssignMenus
             * @summary 为企业分配菜单
             * @request POST:/tenant/{id}/menus
             */
            assignMenus: (id, data, params = {}) => this.request({
                path: `/tenant/${id}/menus`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name GetAssignedMenuCodes
             * @summary 获取企业已分配的菜单编码列表
             * @request GET:/tenant/{id}/menu-codes
             */
            getAssignedMenuCodes: (id, params = {}) => this.request({
                path: `/tenant/${id}/menu-codes`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name GetMyThirdPartyConfigs
             * @summary 获取当前企业第三方平台配置列表（企业管理员）
             * @request GET:/tenant/third-party-configs/current
             */
            getMyThirdPartyConfigs: (params = {}) => this.request({
                path: `/tenant/third-party-configs/current`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 企业
             * @name GetThirdPartyConfigs
             * @summary 获取指定企业第三方平台配置列表（平台管理员）
             * @request GET:/tenant/{id}/third-party-configs
             */
            getThirdPartyConfigs: (id, params = {}) => this.request({
                path: `/tenant/${id}/third-party-configs`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * @description 各平台 config 字段：WECOM(corpId,agentId,appSecret,webhookUrl) | DINGTALK(appKey,appSecret,agentId,webhookToken,webhookSecret) | FEISHU(appId,appSecret,webhookUrl) | WECHAT(mpAppId,mpAppSecret,miniAppId,miniAppSecret)
             *
             * @tags 企业
             * @name SaveThirdPartyConfig
             * @summary 保存第三方平台配置（平台管理员）
             * @request PUT:/tenant/{id}/third-party-configs/{platform}
             */
            saveThirdPartyConfig: (id, platform, data, params = {}) => this.request({
                path: `/tenant/${id}/third-party-configs/${platform}`,
                method: 'PUT',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.messagesInbox = {
            /**
             * No description
             *
             * @tags 站内信
             * @name FindMyMessage
             * @summary 查询我的消息列表（分页，仅消息，不含待办）
             * @request GET:/messages-inbox
             */
            findMyMessage: (query, params = {}) => this.request({
                path: `/messages-inbox`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name RemoveMany
             * @summary 批量删除消息
             * @request DELETE:/messages-inbox
             */
            removeMany: (data, params = {}) => this.request({
                path: `/messages-inbox`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name GetUnreadMessageCount
             * @summary 查询未读消息数量（仅消息，不含待办）
             * @request GET:/messages-inbox/unread-count
             */
            getUnreadMessageCount: (params = {}) => this.request({
                path: `/messages-inbox/unread-count`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name FindMyTodo
             * @summary 查询我的待办列表（分页）
             * @request GET:/messages-inbox/my/todo
             */
            findMyTodo: (query, params = {}) => this.request({
                path: `/messages-inbox/my/todo`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name GetTodoCount
             * @summary 查询我的待办数量（红点）
             * @request GET:/messages-inbox/my/todo/count
             */
            getTodoCount: (params = {}) => this.request({
                path: `/messages-inbox/my/todo/count`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name FindOne
             * @summary 查看消息详情（自动标记已读）
             * @request GET:/messages-inbox/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/messages-inbox/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name Remove
             * @summary 删除单条消息
             * @request DELETE:/messages-inbox/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/messages-inbox/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name MarkRead
             * @summary 标记单条已读
             * @request PATCH:/messages-inbox/{id}/read
             */
            markRead: (id, params = {}) => this.request({
                path: `/messages-inbox/${id}/read`,
                method: 'PATCH',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name MarkAllRead
             * @summary 全部标记已读
             * @request PATCH:/messages-inbox/read-all
             */
            markAllRead: (params = {}) => this.request({
                path: `/messages-inbox/read-all`,
                method: 'PATCH',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 站内信
             * @name Handle
             * @summary 处理单条待办
             * @request POST:/messages-inbox/{id}/handle
             */
            handle: (id, data, params = {}) => this.request({
                path: `/messages-inbox/${id}/handle`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.operationLogs = {
            /**
             * No description
             *
             * @tags 操作日志
             * @name GetMeta
             * @summary 操作日志元数据
             * @request GET:/operation-logs/meta
             */
            getMeta: (params = {}) => this.request({
                path: `/operation-logs/meta`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 操作日志
             * @name FindBusinessDetailLogs
             * @summary 查询业务详情流程与操作日志
             * @request GET:/operation-logs/business/{businessType}/{businessId}/detail-logs
             */
            findBusinessDetailLogs: (businessType, businessId, params = {}) => this.request({
                path: `/operation-logs/business/${businessType}/${businessId}/detail-logs`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 操作日志
             * @name FindBusinessHistory
             * @summary 查询业务对象操作历史
             * @request GET:/operation-logs/business/{businessType}/{businessId}
             */
            findBusinessHistory: (businessType, businessId, params = {}) => this.request({
                path: `/operation-logs/business/${businessType}/${businessId}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 操作日志
             * @name FindOne
             * @summary 操作日志详情
             * @request GET:/operation-logs/detail/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/operation-logs/detail/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 操作日志
             * @name FindMany
             * @summary 分页查询操作日志
             * @request GET:/operation-logs
             */
            findMany: (query, params = {}) => this.request({
                path: `/operation-logs`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.contract = {
            /**
             * No description
             *
             * @tags 合同
             * @name Create
             * @summary 创建合同（含车辆明细）
             * @request POST:/contract
             */
            create: (data, params = {}) => this.request({
                path: `/contract`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindMany
             * @summary 分页查询合同列表
             * @request GET:/contract
             */
            findMany: (query, params = {}) => this.request({
                path: `/contract`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name Update
             * @summary 更新合同（含车辆明细差量同步）
             * @request PATCH:/contract/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/contract/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindOne
             * @summary 查询合同详情（含车辆明细）
             * @request GET:/contract/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/contract/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name Remove
             * @summary 删除合同及关联数据
             * @request DELETE:/contract/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/contract/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindPartyANames
             * @summary 获取合同甲方名称列表
             * @request GET:/contract/party-a-names
             */
            findPartyANames: (params = {}) => this.request({
                path: `/contract/party-a-names`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindPartyBNames
             * @summary 获取合同乙方名称列表
             * @request GET:/contract/party-b-names
             */
            findPartyBNames: (params = {}) => this.request({
                path: `/contract/party-b-names`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindPaymentCandidateItems
             * @summary 查询合同可付款车辆明细
             * @request GET:/contract/{id}/payment-candidate-items
             */
            findPaymentCandidateItems: (id, params = {}) => this.request({
                path: `/contract/${id}/payment-candidate-items`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name StartExportLicenseWorkflow
             * @summary 发起办理出口许可证流程
             * @request POST:/contract/{id}/export-license-workflow
             */
            startExportLicenseWorkflow: (id, data, params = {}) => this.request({
                path: `/contract/${id}/export-license-workflow`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindWorkflowTimeline
             * @summary 查询合同流程日志
             * @request GET:/contract/{id}/workflow-timeline
             */
            findWorkflowTimeline: (id, params = {}) => this.request({
                path: `/contract/${id}/workflow-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 合同
             * @name FindOperationTimeline
             * @summary 查询合同操作历史
             * @request GET:/contract/{id}/operation-timeline
             */
            findOperationTimeline: (id, params = {}) => this.request({
                path: `/contract/${id}/operation-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.workflowEvent = {
            /**
             * No description
             *
             * @tags 流程事件处理器
             * @name FindAll
             * @summary 获取内部事件处理器列表
             * @request GET:/workflow-event/handlers
             */
            findAll: (params = {}) => this.request({
                path: `/workflow-event/handlers`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.workflowInstance = {
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name FindMany
             * @summary 分页查询流程实例列表
             * @request GET:/workflow-instance/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/workflow-instance/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name AdminForceAdvance
             * @summary 管理员强制推进流程
             * @request POST:/workflow-instance/{id}/admin/force-advance
             */
            adminForceAdvance: (id, data, params = {}) => this.request({
                path: `/workflow-instance/${id}/admin/force-advance`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name AdminForceTerminate
             * @summary 管理员强制终止流程
             * @request POST:/workflow-instance/{id}/admin/force-terminate
             */
            adminForceTerminate: (id, data, params = {}) => this.request({
                path: `/workflow-instance/${id}/admin/force-terminate`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name AdminChangeAssignee
             * @summary 管理员修改处理人
             * @request POST:/workflow-instance/{id}/admin/change-assignee
             */
            adminChangeAssignee: (id, data, params = {}) => this.request({
                path: `/workflow-instance/${id}/admin/change-assignee`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name FindTasks
             * @summary 查询当前实例任务列表
             * @request GET:/workflow-instance/{id}/tasks
             */
            findTasks: (id, query, params = {}) => this.request({
                path: `/workflow-instance/${id}/tasks`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程实例（管理端）
             * @name FindOne
             * @summary 查询流程实例详情（含时间线）
             * @request GET:/workflow-instance/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/workflow-instance/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.workflowInstanceCenter = {
            /**
             * No description
             *
             * @tags 我的申请（个人中心）
             * @name Start
             * @summary 发起流程
             * @request POST:/workflow-instance-center/start
             */
            start: (data, params = {}) => this.request({
                path: `/workflow-instance-center/start`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的申请（个人中心）
             * @name Revoke
             * @summary 撤回申请
             * @request POST:/workflow-instance-center/{id}/revoke
             */
            revoke: (id, params = {}) => this.request({
                path: `/workflow-instance-center/${id}/revoke`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的申请（个人中心）
             * @name FindMine
             * @summary 我的申请列表
             * @request GET:/workflow-instance-center/mine/find/many
             */
            findMine: (query, params = {}) => this.request({
                path: `/workflow-instance-center/mine/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的申请（个人中心）
             * @name FindOne
             * @summary 查看申请详情
             * @request GET:/workflow-instance-center/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/workflow-instance-center/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.vehicles = {
            /**
             * No description
             *
             * @tags 车辆主档
             * @name Create
             * @summary 创建车辆主档
             * @request POST:/vehicles
             */
            create: (data, params = {}) => this.request({
                path: `/vehicles`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆主档
             * @name FindMany
             * @summary 分页查询车辆主档
             * @request GET:/vehicles
             */
            findMany: (query, params = {}) => this.request({
                path: `/vehicles`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆主档
             * @name SaveRecognizedAttachments
             * @summary 保存识别后的车辆主档附件
             * @request POST:/vehicles/attachments/recognized
             */
            saveRecognizedAttachments: (data, params = {}) => this.request({
                path: `/vehicles/attachments/recognized`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆主档
             * @name FindOne
             * @summary 查询车辆主档详情
             * @request GET:/vehicles/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/vehicles/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆主档
             * @name Update
             * @summary 更新车辆主档
             * @request PATCH:/vehicles/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/vehicles/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.invoiceApplication = {
            /**
             * No description
             *
             * @tags 开票申请
             * @name Create
             * @summary 创建并提交开票申请
             * @request POST:/invoice-application
             */
            create: (data, params = {}) => this.request({
                path: `/invoice-application`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 开票申请
             * @name FindMany
             * @summary 分页查询开票申请
             * @request GET:/invoice-application
             */
            findMany: (query, params = {}) => this.request({
                path: `/invoice-application`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 开票申请
             * @name FindOne
             * @summary 查询开票申请详情
             * @request GET:/invoice-application/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/invoice-application/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 开票申请
             * @name Update
             * @summary 修改拒绝态开票申请并重新提交
             * @request PATCH:/invoice-application/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/invoice-application/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 开票申请
             * @name Remove
             * @summary 删除拒绝态开票申请
             * @request DELETE:/invoice-application/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/invoice-application/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.carModels = {
            /**
             * No description
             *
             * @tags 车型管理
             * @name Create
             * @summary 创建车型
             * @request POST:/car-models
             */
            create: (data, params = {}) => this.request({
                path: `/car-models`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name FindMany
             * @summary 分页查询车型列表
             * @request GET:/car-models
             */
            findMany: (query, params = {}) => this.request({
                path: `/car-models`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name RemoveMany
             * @summary 批量删除车型
             * @request DELETE:/car-models
             */
            removeMany: (data, params = {}) => this.request({
                path: `/car-models`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name FindAll
             * @summary 查询所有车型
             * @request GET:/car-models/find/all
             */
            findAll: (params = {}) => this.request({
                path: `/car-models/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name FindOne
             * @summary 查询车型详情
             * @request GET:/car-models/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/car-models/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name Update
             * @summary 更新车型
             * @request PATCH:/car-models/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/car-models/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车型管理
             * @name Remove
             * @summary 删除车型
             * @request DELETE:/car-models/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/car-models/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.company = {
            /**
             * No description
             *
             * @tags 公司管理
             * @name Create
             * @summary 创建公司
             * @request POST:/company
             */
            create: (data, params = {}) => this.request({
                path: `/company`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name FindMany
             * @summary 分页查询公司列表
             * @request GET:/company
             */
            findMany: (query, params = {}) => this.request({
                path: `/company`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name RemoveMany
             * @summary 批量删除公司
             * @request DELETE:/company
             */
            removeMany: (data, params = {}) => this.request({
                path: `/company`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name FindOptions
             * @summary 查询公司选项
             * @request GET:/company/options
             */
            findOptions: (query, params = {}) => this.request({
                path: `/company/options`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name FindOne
             * @summary 查询公司详情
             * @request GET:/company/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/company/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name Update
             * @summary 更新公司
             * @request PATCH:/company/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/company/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公司管理
             * @name Remove
             * @summary 删除公司
             * @request DELETE:/company/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/company/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.person = {
            /**
             * No description
             *
             * @tags 人员信息
             * @name Create
             * @summary 创建人员
             * @request POST:/person
             */
            create: (data, params = {}) => this.request({
                path: `/person`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name FindMany
             * @summary 分页查询人员列表
             * @request GET:/person
             */
            findMany: (query, params = {}) => this.request({
                path: `/person`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name RemoveMany
             * @summary 批量删除人员
             * @request DELETE:/person
             */
            removeMany: (data, params = {}) => this.request({
                path: `/person`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name FindOptions
             * @summary 查询人员选项
             * @request GET:/person/options
             */
            findOptions: (query, params = {}) => this.request({
                path: `/person/options`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name FindOne
             * @summary 查询人员详情
             * @request GET:/person/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/person/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name Update
             * @summary 更新人员
             * @request PATCH:/person/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/person/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 人员信息
             * @name Remove
             * @summary 删除人员
             * @request DELETE:/person/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/person/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.serviceContracts = {
            /**
             * No description
             *
             * @tags 服务合同
             * @name Create
             * @summary 创建服务合同
             * @request POST:/service-contracts
             */
            create: (data, params = {}) => this.request({
                path: `/service-contracts`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 服务合同
             * @name FindMany
             * @summary 分页查询服务合同
             * @request GET:/service-contracts
             */
            findMany: (query, params = {}) => this.request({
                path: `/service-contracts`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 服务合同
             * @name FindOne
             * @summary 查询服务合同详情
             * @request GET:/service-contracts/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/service-contracts/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 服务合同
             * @name Update
             * @summary 修改服务合同
             * @request PATCH:/service-contracts/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/service-contracts/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 服务合同
             * @name Remove
             * @summary 删除服务合同
             * @request DELETE:/service-contracts/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/service-contracts/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.workflowDefinition = {
            /**
             * No description
             *
             * @tags 流程定义
             * @name Create
             * @summary 创建流程定义
             * @request POST:/workflow-definition
             */
            create: (data, params = {}) => this.request({
                path: `/workflow-definition`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name RemoveMany
             * @summary 批量删除流程定义
             * @request DELETE:/workflow-definition
             */
            removeMany: (data, params = {}) => this.request({
                path: `/workflow-definition`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindMany
             * @summary 分页查询流程定义
             * @request GET:/workflow-definition/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/workflow-definition/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindOptions
             * @summary 查询流程定义选项
             * @request GET:/workflow-definition/options
             */
            findOptions: (query, params = {}) => this.request({
                path: `/workflow-definition/options`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindByCodes
             * @summary 按编码批量查询流程定义
             * @request GET:/workflow-definition/find/by-codes
             */
            findByCodes: (query, params = {}) => this.request({
                path: `/workflow-definition/find/by-codes`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindOne
             * @summary 查询流程定义详情（含草稿）
             * @request GET:/workflow-definition/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/workflow-definition/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name Update
             * @summary 更新流程定义基础信息
             * @request PATCH:/workflow-definition/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/workflow-definition/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name Remove
             * @summary 删除流程定义
             * @request DELETE:/workflow-definition/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/workflow-definition/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name SaveDraft
             * @summary 保存草稿
             * @request POST:/workflow-definition/{id}/draft
             */
            saveDraft: (id, data, params = {}) => this.request({
                path: `/workflow-definition/${id}/draft`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name Publish
             * @summary 发布流程
             * @request POST:/workflow-definition/{id}/publish
             */
            publish: (id, data, params = {}) => this.request({
                path: `/workflow-definition/${id}/publish`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name Disable
             * @summary 停用流程
             * @request POST:/workflow-definition/{id}/disable
             */
            disable: (id, params = {}) => this.request({
                path: `/workflow-definition/${id}/disable`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindVersionList
             * @summary 获取版本列表
             * @request GET:/workflow-definition/{id}/versions
             */
            findVersionList: (id, params = {}) => this.request({
                path: `/workflow-definition/${id}/versions`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程定义
             * @name FindVersion
             * @summary 获取指定版本DSL
             * @request GET:/workflow-definition/{id}/versions/{version}
             */
            findVersion: (id, version, params = {}) => this.request({
                path: `/workflow-definition/${id}/versions/${version}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程事件动作
             * @name CreateEvent
             * @summary 创建事件动作草稿
             * @request POST:/workflow-definition/{defId}/event-actions
             */
            createEvent: (defId, data, params = {}) => this.request({
                path: `/workflow-definition/${defId}/event-actions`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程事件动作
             * @name FindEventDrafts
             * @summary 事件动作草稿列表
             * @request GET:/workflow-definition/{defId}/event-actions
             */
            findEventDrafts: (defId, params = {}) => this.request({
                path: `/workflow-definition/${defId}/event-actions`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程事件动作
             * @name FindVersionActions
             * @summary 事件动作版本快照列表
             * @request GET:/workflow-definition/{defId}/event-actions/versions/{version}
             */
            findVersionActions: (defId, version, params = {}) => this.request({
                path: `/workflow-definition/${defId}/event-actions/versions/${version}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程事件动作
             * @name UpdateEvent
             * @summary 更新事件动作草稿
             * @request PATCH:/workflow-definition/{defId}/event-actions/{id}
             */
            updateEvent: (defId, id, data, params = {}) => this.request({
                path: `/workflow-definition/${defId}/event-actions/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程事件动作
             * @name RemoveEvent
             * @summary 删除事件动作草稿
             * @request DELETE:/workflow-definition/{defId}/event-actions/{id}
             */
            removeEvent: (defId, id, params = {}) => this.request({
                path: `/workflow-definition/${defId}/event-actions/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.systemSettings = {
            /**
             * No description
             *
             * @tags 系统设置
             * @name GetTenantProfile
             * @summary 获取当前企业资料
             * @request GET:/system-settings/tenant-profile
             */
            getTenantProfile: (params = {}) => this.request({
                path: `/system-settings/tenant-profile`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 系统设置
             * @name UpdateTenantProfile
             * @summary 更新当前企业资料
             * @request PATCH:/system-settings/tenant-profile
             */
            updateTenantProfile: (data, params = {}) => this.request({
                path: `/system-settings/tenant-profile`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 系统设置
             * @name GetThirdPartyConfigs
             * @summary 获取当前企业第三方配置
             * @request GET:/system-settings/third-party-configs
             */
            getThirdPartyConfigs: (params = {}) => this.request({
                path: `/system-settings/third-party-configs`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 系统设置
             * @name SaveThirdPartyConfig
             * @summary 保存当前企业第三方配置
             * @request PUT:/system-settings/third-party-configs/{platform}
             */
            saveThirdPartyConfig: (platform, data, params = {}) => this.request({
                path: `/system-settings/third-party-configs/${platform}`,
                method: 'PUT',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 系统设置
             * @name TestThirdPartyConfig
             * @summary 测试当前企业第三方配置
             * @request POST:/system-settings/third-party-configs/{platform}/test
             */
            testThirdPartyConfig: (platform, params = {}) => this.request({
                path: `/system-settings/third-party-configs/${platform}/test`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 系统设置
             * @name GetCapabilities
             * @summary 获取当前企业能力清单
             * @request GET:/system-settings/capabilities
             */
            getCapabilities: (params = {}) => this.request({
                path: `/system-settings/capabilities`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.columnLevelSecurity = {
            /**
             * No description
             *
             * @tags 数据权限
             * @name GetMeta
             * @summary 获取可配置元数据（资源与字段）
             * @request GET:/column-level-security/meta
             */
            getMeta: (params = {}) => this.request({
                path: `/column-level-security/meta`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 数据权限
             * @name GetMaskRules
             * @summary 获取脱敏规则清单
             * @request GET:/column-level-security/mask-rules
             */
            getMaskRules: (params = {}) => this.request({
                path: `/column-level-security/mask-rules`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 数据权限
             * @name GetRules
             * @summary 获取当前企业已配置规则
             * @request GET:/column-level-security/rules
             */
            getRules: (query, params = {}) => this.request({
                path: `/column-level-security/rules`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 数据权限
             * @name Update
             * @summary 保存规则（整体覆盖）
             * @request PUT:/column-level-security/rules
             */
            update: (data, params = {}) => this.request({
                path: `/column-level-security/rules`,
                method: 'PUT',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.rowLevelSecurity = {
            /**
             * No description
             *
             * @tags 行级数据隔离
             * @name GetMeta
             * @summary 获取参与行级隔离的表列表
             * @request GET:/row-level-security/meta
             */
            getMeta: (params = {}) => this.request({
                path: `/row-level-security/meta`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 行级数据隔离
             * @name GetRules
             * @summary 获取当前企业已配置规则
             * @request GET:/row-level-security/rules
             */
            getRules: (query, params = {}) => this.request({
                path: `/row-level-security/rules`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 行级数据隔离
             * @name SaveRules
             * @summary 保存规则（整体覆盖指定表）
             * @request PUT:/row-level-security/rules
             */
            saveRules: (data, params = {}) => this.request({
                path: `/row-level-security/rules`,
                method: 'PUT',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.userPreferences = {
            /**
             * No description
             *
             * @tags 用户个性化配置
             * @name Save
             * @summary 保存/更新个性化配置（upsert）
             * @request POST:/user-preferences
             */
            save: (data, params = {}) => this.request({
                path: `/user-preferences`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户个性化配置
             * @name FindMany
             * @summary 查询配置列表
             * @request GET:/user-preferences
             */
            findMany: (query, params = {}) => this.request({
                path: `/user-preferences`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户个性化配置
             * @name Remove
             * @summary 删除配置
             * @request DELETE:/user-preferences
             */
            remove: (query, params = {}) => this.request({
                path: `/user-preferences`,
                method: 'DELETE',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 用户个性化配置
             * @name FindOne
             * @summary 查询单条配置
             * @request GET:/user-preferences/one
             */
            findOne: (query, params = {}) => this.request({
                path: `/user-preferences/one`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.docxTemplates = {
            /**
             * No description
             *
             * @tags 模板管理
             * @name Create
             * @summary 创建模板
             * @request POST:/docx-templates
             */
            create: (data, params = {}) => this.request({
                path: `/docx-templates`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name FindMany
             * @summary 分页查询模板
             * @request GET:/docx-templates
             */
            findMany: (query, params = {}) => this.request({
                path: `/docx-templates`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name RemoveMany
             * @summary 批量删除模板
             * @request DELETE:/docx-templates
             */
            removeMany: (data, params = {}) => this.request({
                path: `/docx-templates`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name FindByBusinessTypes
             * @summary 按业务归属批量查询模板
             * @request GET:/docx-templates/find/by-business-types
             */
            findByBusinessTypes: (query, params = {}) => this.request({
                path: `/docx-templates/find/by-business-types`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name FindOne
             * @summary 查询模板详情
             * @request GET:/docx-templates/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/docx-templates/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name Update
             * @summary 更新模板
             * @request PATCH:/docx-templates/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/docx-templates/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name Remove
             * @summary 删除模板
             * @request DELETE:/docx-templates/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/docx-templates/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 模板管理
             * @name Test
             * @summary 测试模板（生成 Word 文档并下载）
             * @request POST:/docx-templates/test
             */
            test: (data, params = {}) => this.request({
                path: `/docx-templates/test`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.ai = {
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeVehicleCertificate
             * @summary 识别车辆合格证并提取车辆信息
             * @request POST:/ai/documents/vehicle-certificate/recognize
             */
            recognizeVehicleCertificate: (data, params = {}) => this.request({
                path: `/ai/documents/vehicle-certificate/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeEnvironmentalList
             * @summary 识别环保清单并提取车辆环保信息
             * @request POST:/ai/documents/environmental-list/recognize
             */
            recognizeEnvironmentalList: (data, params = {}) => this.request({
                path: `/ai/documents/environmental-list/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeExportLicense
             * @summary 识别出口许可证并提取许可证信息
             * @request POST:/ai/documents/export-license/recognize
             */
            recognizeExportLicense: (data, params = {}) => this.request({
                path: `/ai/documents/export-license/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeCustomsDeclaration
             * @summary 识别报关单并提取报关与车辆信息
             * @request POST:/ai/documents/customs-declaration/recognize
             */
            recognizeCustomsDeclaration: (data, params = {}) => this.request({
                path: `/ai/documents/customs-declaration/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeVehicleCancellation
             * @summary 识别车辆注销证明并提取注销与车辆信息
             * @request POST:/ai/documents/vehicle-cancellation/recognize
             */
            recognizeVehicleCancellation: (data, params = {}) => this.request({
                path: `/ai/documents/vehicle-cancellation/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeText
             * @summary 按类型识别文本候选结果
             * @request POST:/ai/text-recognitions/recognize
             */
            recognizeText: (data, params = {}) => this.request({
                path: `/ai/text-recognitions/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags AI
             * @name RecognizeVehicleAttachment
             * @summary 按车辆附件类型识别车辆附件
             * @request POST:/ai/vehicle-attachments/recognize
             */
            recognizeVehicleAttachment: (data, params = {}) => this.request({
                path: `/ai/vehicle-attachments/recognize`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.document = {
            /**
             * No description
             *
             * @tags 单证管理
             * @name FindMany
             * @summary 分页查询单证管理车辆列表
             * @request GET:/document
             */
            findMany: (query, params = {}) => this.request({
                path: `/document`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 单证管理
             * @name UpdateVehicleField
             * @summary 按车架号批量修正车辆字段
             * @request PATCH:/document/vehicles/{vin}/field
             */
            updateVehicleField: (vin, data, params = {}) => this.request({
                path: `/document/vehicles/${vin}/field`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.common = {
            /**
             * No description
             *
             * @tags 公共接口
             * @name GetOssSignature
             * @summary 获取 OSS 客户端直传签名
             * @request GET:/common/oss/signature
             */
            getOssSignature: (query, params = {}) => this.request({
                path: `/common/oss/signature`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 公共接口
             * @name GetOssSignatureUrl
             * @summary 通过 object key 访问私有 OSS 资源（302 重定向到签名 URL）
             * @request GET:/common/ali-oss/{splat}
             */
            getOssSignatureUrl: (splat, query, params = {}) => this.request({
                path: `/common/ali-oss/${splat}`,
                method: 'GET',
                query: query,
                ...params,
            }),
        };
        this.health = {
            /**
             * No description
             *
             * @tags 健康检查
             * @name Health
             * @summary 健康检查 / 探活
             * @request GET:/health
             */
            health: (params = {}) => this.request({
                path: `/health`,
                method: 'GET',
                ...params,
            }),
        };
        this.carSources = {
            /**
             * No description
             *
             * @tags 车源发布
             * @name Create
             * @summary 创建车源
             * @request POST:/car-sources
             */
            create: (data, params = {}) => this.request({
                path: `/car-sources`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindMany
             * @summary 分页查询车源管理列表
             * @request GET:/car-sources
             */
            findMany: (query, params = {}) => this.request({
                path: `/car-sources`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name ImportMany
             * @summary 单事务批量导入车源
             * @request POST:/car-sources/import
             */
            importMany: (data, params = {}) => this.request({
                path: `/car-sources/import`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name Search
             * @summary POST 分页查询车源管理列表
             * @request POST:/car-sources/search
             */
            search: (data, params = {}) => this.request({
                path: `/car-sources/search`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name ExportMany
             * @summary 导出符合管理端查询条件的全部车源
             * @request GET:/car-sources/export/many
             */
            exportMany: (query, params = {}) => this.request({
                path: `/car-sources/export/many`,
                method: 'GET',
                query: query,
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdateExportPrice
             * @summary 批量更新车源出口价格
             * @request PATCH:/car-sources/export-price/batch
             */
            batchUpdateExportPrice: (data, params = {}) => this.request({
                path: `/car-sources/export-price/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdateAttachments
             * @summary 批量替换或追加车源附件
             * @request PATCH:/car-sources/attachments/batch
             */
            batchUpdateAttachments: (data, params = {}) => this.request({
                path: `/car-sources/attachments/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdateWechatQr
             * @summary 批量替换车源联系人微信二维码
             * @request PATCH:/car-sources/wechat-qr/batch
             */
            batchUpdateWechatQr: (data, params = {}) => this.request({
                path: `/car-sources/wechat-qr/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdateContactPhone
             * @summary 批量修改车源联系人手机号
             * @request PATCH:/car-sources/contact-phone/batch
             */
            batchUpdateContactPhone: (data, params = {}) => this.request({
                path: `/car-sources/contact-phone/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdateFields
             * @summary 批量修改车源基础字段
             * @request PATCH:/car-sources/fields/batch
             */
            batchUpdateFields: (data, params = {}) => this.request({
                path: `/car-sources/fields/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name RemoveMany
             * @summary 单事务批量删除车源
             * @request DELETE:/car-sources/batch
             */
            removeMany: (data, params = {}) => this.request({
                path: `/car-sources/batch`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindContactDefaults
             * @summary 查询发布人历史联系方式默认值
             * @request GET:/car-sources/contact-defaults
             */
            findContactDefaults: (query, params = {}) => this.request({
                path: `/car-sources/contact-defaults`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindShareBootstrap
             * @summary 查询公开车源分享筛选项
             * @request GET:/car-sources/share/bootstrap
             */
            findShareBootstrap: (query, params = {}) => this.request({
                path: `/car-sources/share/bootstrap`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindShareTags
             * @summary 查询公开车源推荐标签
             * @request GET:/car-sources/share/tags
             */
            findShareTags: (query, params = {}) => this.request({
                path: `/car-sources/share/tags`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindShareTagVehicles
             * @summary 查询公开标签的全部车源
             * @request GET:/car-sources/share/tags/{tagId}/vehicles
             */
            findShareTagVehicles: (tagId, query, params = {}) => this.request({
                path: `/car-sources/share/tags/${tagId}/vehicles`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindShareMany
             * @summary 分页查询公开在架车源
             * @request GET:/car-sources/share
             */
            findShareMany: (query, params = {}) => this.request({
                path: `/car-sources/share`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindShareDetail
             * @summary 查询公开在架车源详情
             * @request GET:/car-sources/share/{id}
             */
            findShareDetail: (id, query, params = {}) => this.request({
                path: `/car-sources/share/${id}`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name FindOne
             * @summary 查询车源详情
             * @request GET:/car-sources/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/car-sources/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name Update
             * @summary 更新车源及图片
             * @request PATCH:/car-sources/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/car-sources/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name Remove
             * @summary 软删除车源
             * @request DELETE:/car-sources/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/car-sources/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name UpdatePublishStatus
             * @summary 更新单条车源发布状态
             * @request PATCH:/car-sources/{id}/publish-status
             */
            updatePublishStatus: (id, data, params = {}) => this.request({
                path: `/car-sources/${id}/publish-status`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源发布
             * @name BatchUpdatePublishStatus
             * @summary 批量更新车源发布状态
             * @request PATCH:/car-sources/publish-status/batch
             */
            batchUpdatePublishStatus: (data, params = {}) => this.request({
                path: `/car-sources/publish-status/batch`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.carSourceTags = {
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name Create
             * @summary 创建车源标签
             * @request POST:/car-source-tags
             */
            create: (data, params = {}) => this.request({
                path: `/car-source-tags`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name FindMany
             * @summary 分页查询车源标签
             * @request GET:/car-source-tags
             */
            findMany: (query, params = {}) => this.request({
                path: `/car-source-tags`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name FindRecommendation
             * @summary 读取企业推荐标签
             * @request GET:/car-source-tags/recommendation
             */
            findRecommendation: (params = {}) => this.request({
                path: `/car-source-tags/recommendation`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name SaveRecommendation
             * @summary 保存企业推荐标签
             * @request PUT:/car-source-tags/recommendation
             */
            saveRecommendation: (data, params = {}) => this.request({
                path: `/car-source-tags/recommendation`,
                method: 'PUT',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name FindOne
             * @summary 查询车源标签详情
             * @request GET:/car-source-tags/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/car-source-tags/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name Update
             * @summary 更新车源标签
             * @request PATCH:/car-source-tags/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/car-source-tags/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源标签管理
             * @name Remove
             * @summary 软删除车源标签
             * @request DELETE:/car-source-tags/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/car-source-tags/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.carSourceShares = {
            /**
             * No description
             *
             * @tags 车源分享管理
             * @name Create
             * @summary 创建车源分享
             * @request POST:/car-source-shares
             */
            create: (data, params = {}) => this.request({
                path: `/car-source-shares`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源分享管理
             * @name FindMany
             * @summary 分页查询车源分享
             * @request GET:/car-source-shares
             */
            findMany: (query, params = {}) => this.request({
                path: `/car-source-shares`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源分享管理
             * @name FindOne
             * @summary 查询车源分享详情
             * @request GET:/car-source-shares/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/car-source-shares/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源分享管理
             * @name Update
             * @summary 更新车源分享
             * @request PATCH:/car-source-shares/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/car-source-shares/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车源分享管理
             * @name Remove
             * @summary 软删除车源分享
             * @request DELETE:/car-source-shares/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/car-source-shares/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.workflowTask = {
            /**
             * No description
             *
             * @tags 任务管理（管理端）
             * @name FindMany
             * @summary 分页查询任务列表
             * @request GET:/workflow-task/find/many
             */
            findMany: (query, params = {}) => this.request({
                path: `/workflow-task/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.workflowTaskInbox = {
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name FindMyPending
             * @summary 待办列表
             * @request GET:/workflow-task-inbox/pending/find/many
             */
            findMyPending: (query, params = {}) => this.request({
                path: `/workflow-task-inbox/pending/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name FindMyDone
             * @summary 已办列表
             * @request GET:/workflow-task-inbox/done/find/many
             */
            findMyDone: (query, params = {}) => this.request({
                path: `/workflow-task-inbox/done/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name FindMyCc
             * @summary 抄送我的列表
             * @request GET:/workflow-task-inbox/cc/find/many
             */
            findMyCc: (query, params = {}) => this.request({
                path: `/workflow-task-inbox/cc/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name MarkCcRead
             * @summary 标记抄送记录已读
             * @request PATCH:/workflow-task-inbox/cc/{id}/read
             */
            markCcRead: (id, params = {}) => this.request({
                path: `/workflow-task-inbox/cc/${id}/read`,
                method: 'PATCH',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name GetPendingCount
             * @summary 待办数量（Badge）
             * @request GET:/workflow-task-inbox/pending/count
             */
            getPendingCount: (params = {}) => this.request({
                path: `/workflow-task-inbox/pending/count`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name FindMyPendingByInstanceIds
             * @summary 按实例查询我的待办任务
             * @request POST:/workflow-task-inbox/pending/by-instance-ids
             */
            findMyPendingByInstanceIds: (data, params = {}) => this.request({
                path: `/workflow-task-inbox/pending/by-instance-ids`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name BatchApprove
             * @summary 批量审批通过
             * @request POST:/workflow-task-inbox/batch/approve
             */
            batchApprove: (data, params = {}) => this.request({
                path: `/workflow-task-inbox/batch/approve`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name FindTaskDetail
             * @summary 任务详情（含字段级权限）
             * @request GET:/workflow-task-inbox/{id}
             */
            findTaskDetail: (id, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name Approve
             * @summary 审批通过
             * @request POST:/workflow-task-inbox/{id}/approve
             */
            approve: (id, data, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}/approve`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name Reject
             * @summary 审批拒绝
             * @request POST:/workflow-task-inbox/{id}/reject
             */
            reject: (id, data, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}/reject`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name Transfer
             * @summary 转办
             * @request POST:/workflow-task-inbox/{id}/transfer
             */
            transfer: (id, data, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}/transfer`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name AddSign
             * @summary 加签
             * @request POST:/workflow-task-inbox/{id}/add-sign
             */
            addSign: (id, data, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}/add-sign`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 待办/已办（个人中心）
             * @name ReturnTo
             * @summary 驳回
             * @request POST:/workflow-task-inbox/{id}/return-to
             */
            returnTo: (id, data, params = {}) => this.request({
                path: `/workflow-task-inbox/${id}/return-to`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.workflowDelegate = {
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name Create
             * @summary 创建委托（当前用户为委托人）
             * @request POST:/workflow-delegate
             */
            create: (data, params = {}) => this.request({
                path: `/workflow-delegate`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name FindMine
             * @summary 查询我的委托列表
             * @request GET:/workflow-delegate
             */
            findMine: (params = {}) => this.request({
                path: `/workflow-delegate`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name AdminFindMany
             * @summary 管理端分页查询委托列表
             * @request GET:/workflow-delegate/admin/find/many
             */
            adminFindMany: (query, params = {}) => this.request({
                path: `/workflow-delegate/admin/find/many`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name AdminCreate
             * @summary 管理端创建委托
             * @request POST:/workflow-delegate/admin
             */
            adminCreate: (data, params = {}) => this.request({
                path: `/workflow-delegate/admin`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name AdminUpdate
             * @summary 管理端修改委托
             * @request PATCH:/workflow-delegate/admin/{id}
             */
            adminUpdate: (id, data, params = {}) => this.request({
                path: `/workflow-delegate/admin/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name AdminRemove
             * @summary 管理端删除委托
             * @request DELETE:/workflow-delegate/admin/{id}
             */
            adminRemove: (id, params = {}) => this.request({
                path: `/workflow-delegate/admin/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name Update
             * @summary 修改委托
             * @request PATCH:/workflow-delegate/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/workflow-delegate/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 工作流委托代理
             * @name Remove
             * @summary 删除委托
             * @request DELETE:/workflow-delegate/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/workflow-delegate/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.workflowUrge = {
            /**
             * No description
             *
             * @tags 流程催办
             * @name Urge
             * @summary 催办流程实例
             * @request POST:/workflow-urge/instance/{instanceId}
             */
            urge: (instanceId, params = {}) => this.request({
                path: `/workflow-urge/instance/${instanceId}`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
        };
        this.workflowMonitor = {
            /**
             * No description
             *
             * @tags 流程监控看板
             * @name GetOverview
             * @summary 总览统计
             * @request GET:/workflow-monitor/overview
             */
            getOverview: (params = {}) => this.request({
                path: `/workflow-monitor/overview`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程监控看板
             * @name GetStatsByDefinition
             * @summary 按流程定义统计
             * @request GET:/workflow-monitor/statistics
             */
            getStatsByDefinition: (params = {}) => this.request({
                path: `/workflow-monitor/statistics`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 流程监控看板
             * @name GetNodeDuration
             * @summary 节点平均处理时长
             * @request GET:/workflow-monitor/node-duration
             */
            getNodeDuration: (params = {}) => this.request({
                path: `/workflow-monitor/node-duration`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.myTodo = {
            /**
             * No description
             *
             * @tags 我的待办
             * @name GetTabCounts
             * @summary 查询我的待办全部 Tab 数量
             * @request GET:/my-todo/tab-counts
             */
            getTabCounts: (params = {}) => this.request({
                path: `/my-todo/tab-counts`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.myCustomer = {
            /**
             * No description
             *
             * @tags 我的客户
             * @name Create
             * @summary 创建我的客户
             * @request POST:/my-customer
             */
            create: (data, params = {}) => this.request({
                path: `/my-customer`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name FindMany
             * @summary 分页查询我的客户列表
             * @request GET:/my-customer
             */
            findMany: (query, params = {}) => this.request({
                path: `/my-customer`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name RemoveMany
             * @summary 批量删除我的客户
             * @request DELETE:/my-customer
             */
            removeMany: (data, params = {}) => this.request({
                path: `/my-customer`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name FindOptions
             * @summary 查询我的客户选项
             * @request GET:/my-customer/options
             */
            findOptions: (query, params = {}) => this.request({
                path: `/my-customer/options`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name FindOne
             * @summary 查询我的客户详情
             * @request GET:/my-customer/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/my-customer/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name Update
             * @summary 更新我的客户
             * @request PATCH:/my-customer/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/my-customer/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 我的客户
             * @name Remove
             * @summary 删除我的客户
             * @request DELETE:/my-customer/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/my-customer/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.proformaInvoice = {
            /**
             * No description
             *
             * @tags 形式发票
             * @name Create
             * @summary 创建形式发票（含车辆明细）
             * @request POST:/proforma-invoice
             */
            create: (data, params = {}) => this.request({
                path: `/proforma-invoice`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 形式发票
             * @name FindMany
             * @summary 分页查询形式发票列表
             * @request GET:/proforma-invoice
             */
            findMany: (query, params = {}) => this.request({
                path: `/proforma-invoice`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 形式发票
             * @name RemoveMany
             * @summary 批量删除形式发票
             * @request DELETE:/proforma-invoice
             */
            removeMany: (data, params = {}) => this.request({
                path: `/proforma-invoice`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 形式发票
             * @name FindOne
             * @summary 查询形式发票详情
             * @request GET:/proforma-invoice/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/proforma-invoice/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 形式发票
             * @name Update
             * @summary 更新形式发票（含车辆明细）
             * @request PATCH:/proforma-invoice/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/proforma-invoice/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 形式发票
             * @name Remove
             * @summary 删除形式发票
             * @request DELETE:/proforma-invoice/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/proforma-invoice/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.purchaseRequirement = {
            /**
             * No description
             *
             * @tags 采购需求单
             * @name Create
             * @summary 创建采购需求单
             * @request POST:/purchase-requirement
             */
            create: (data, params = {}) => this.request({
                path: `/purchase-requirement`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 采购需求单
             * @name FindMany
             * @summary 分页查询采购需求单
             * @request GET:/purchase-requirement
             */
            findMany: (query, params = {}) => this.request({
                path: `/purchase-requirement`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 采购需求单
             * @name FindOne
             * @summary 查询采购需求单详情
             * @request GET:/purchase-requirement/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/purchase-requirement/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 采购需求单
             * @name Update
             * @summary 修改采购需求单
             * @request PATCH:/purchase-requirement/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/purchase-requirement/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 采购需求单
             * @name Remove
             * @summary 删除采购需求单及关联数据
             * @request DELETE:/purchase-requirement/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/purchase-requirement/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 采购需求单
             * @name Submit
             * @summary 提交采购需求单审批
             * @request POST:/purchase-requirement/{id}/submit
             */
            submit: (id, params = {}) => this.request({
                path: `/purchase-requirement/${id}/submit`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
        };
        this.preReceivedDeposit = {
            /**
             * No description
             *
             * @tags 预收定金
             * @name Create
             * @summary 创建预收定金
             * @request POST:/pre-received-deposit
             */
            create: (data, params = {}) => this.request({
                path: `/pre-received-deposit`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name FindMany
             * @summary 分页查询预收定金
             * @request GET:/pre-received-deposit
             */
            findMany: (query, params = {}) => this.request({
                path: `/pre-received-deposit`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name FindOne
             * @summary 查询预收定金详情
             * @request GET:/pre-received-deposit/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/pre-received-deposit/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name Update
             * @summary 修改预收定金
             * @request PATCH:/pre-received-deposit/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/pre-received-deposit/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name Cancel
             * @summary 取消预收定金
             * @request POST:/pre-received-deposit/{id}/cancel
             */
            cancel: (id, params = {}) => this.request({
                path: `/pre-received-deposit/${id}/cancel`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name ApplyReceipt
             * @summary 申请收定金
             * @request POST:/pre-received-deposit/{id}/apply-receipt
             */
            applyReceipt: (id, params = {}) => this.request({
                path: `/pre-received-deposit/${id}/apply-receipt`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 预收定金
             * @name ApplyRefund
             * @summary 申请退定金
             * @request POST:/pre-received-deposit/{id}/apply-refund
             */
            applyRefund: (id, params = {}) => this.request({
                path: `/pre-received-deposit/${id}/apply-refund`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
        };
        this.fundTransaction = {
            /**
             * No description
             *
             * @tags 资金交易
             * @name Create
             * @summary 创建资金交易
             * @request POST:/fund-transaction
             */
            create: (data, params = {}) => this.request({
                path: `/fund-transaction`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name FindMany
             * @summary 分页查询资金交易列表
             * @request GET:/fund-transaction
             */
            findMany: (query, params = {}) => this.request({
                path: `/fund-transaction`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name Update
             * @summary 更新资金交易
             * @request PATCH:/fund-transaction/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/fund-transaction/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name FindOne
             * @summary 查询资金交易详情
             * @request GET:/fund-transaction/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/fund-transaction/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name Remove
             * @summary 删除资金交易
             * @request DELETE:/fund-transaction/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/fund-transaction/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name Submit
             * @summary 提交资金交易审批
             * @request POST:/fund-transaction/{id}/submit
             */
            submit: (id, params = {}) => this.request({
                path: `/fund-transaction/${id}/submit`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name CreateSettlementBatch
             * @summary 新增资金结算批次
             * @request POST:/fund-transaction/{id}/settlement-batches
             */
            createSettlementBatch: (id, data, params = {}) => this.request({
                path: `/fund-transaction/${id}/settlement-batches`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name FindSettlementBatches
             * @summary 查询资金结算批次历史
             * @request GET:/fund-transaction/{id}/settlement-batches
             */
            findSettlementBatches: (id, params = {}) => this.request({
                path: `/fund-transaction/${id}/settlement-batches`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 资金交易
             * @name FindSettlementHistory
             * @summary 分页查询业务实际资金记录
             * @request GET:/fund-transaction/settlement-history
             */
            findSettlementHistory: (query, params = {}) => this.request({
                path: `/fund-transaction/settlement-history`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.baseData = {
            /**
             * No description
             *
             * @tags 币种管理
             * @name CreateCurrency
             * @summary 创建币种
             * @request POST:/base-data/currencies
             */
            createCurrency: (data, params = {}) => this.request({
                path: `/base-data/currencies`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name FindManyCurrencies
             * @summary 分页查询币种列表
             * @request GET:/base-data/currencies
             */
            findManyCurrencies: (query, params = {}) => this.request({
                path: `/base-data/currencies`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name RemoveManyCurrencies
             * @summary 批量删除币种
             * @request DELETE:/base-data/currencies
             */
            removeManyCurrencies: (data, params = {}) => this.request({
                path: `/base-data/currencies`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name FindAllCurrencies
             * @summary 查询所有币种
             * @request GET:/base-data/currencies/find/all
             */
            findAllCurrencies: (params = {}) => this.request({
                path: `/base-data/currencies/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name FindOneCurrency
             * @summary 查询币种详情
             * @request GET:/base-data/currencies/{id}
             */
            findOneCurrency: (id, params = {}) => this.request({
                path: `/base-data/currencies/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name UpdateCurrency
             * @summary 更新币种
             * @request PATCH:/base-data/currencies/{id}
             */
            updateCurrency: (id, data, params = {}) => this.request({
                path: `/base-data/currencies/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 币种管理
             * @name RemoveCurrency
             * @summary 删除币种
             * @request DELETE:/base-data/currencies/{id}
             */
            removeCurrency: (id, params = {}) => this.request({
                path: `/base-data/currencies/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name CreateExportCountry
             * @summary 创建出口国
             * @request POST:/base-data/export-countries
             */
            createExportCountry: (data, params = {}) => this.request({
                path: `/base-data/export-countries`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name FindManyExportCountries
             * @summary 分页查询出口国列表
             * @request GET:/base-data/export-countries
             */
            findManyExportCountries: (query, params = {}) => this.request({
                path: `/base-data/export-countries`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name RemoveManyExportCountries
             * @summary 批量删除出口国
             * @request DELETE:/base-data/export-countries
             */
            removeManyExportCountries: (data, params = {}) => this.request({
                path: `/base-data/export-countries`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name FindAllExportCountries
             * @summary 查询所有出口国
             * @request GET:/base-data/export-countries/find/all
             */
            findAllExportCountries: (params = {}) => this.request({
                path: `/base-data/export-countries/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name FindOneExportCountry
             * @summary 查询出口国详情
             * @request GET:/base-data/export-countries/{id}
             */
            findOneExportCountry: (id, params = {}) => this.request({
                path: `/base-data/export-countries/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name UpdateExportCountry
             * @summary 更新出口国
             * @request PATCH:/base-data/export-countries/{id}
             */
            updateExportCountry: (id, data, params = {}) => this.request({
                path: `/base-data/export-countries/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口国管理
             * @name RemoveExportCountry
             * @summary 删除出口国
             * @request DELETE:/base-data/export-countries/{id}
             */
            removeExportCountry: (id, params = {}) => this.request({
                path: `/base-data/export-countries/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name CreateExportPort
             * @summary 创建出口港
             * @request POST:/base-data/export-ports
             */
            createExportPort: (data, params = {}) => this.request({
                path: `/base-data/export-ports`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name FindManyExportPorts
             * @summary 分页查询出口港列表
             * @request GET:/base-data/export-ports
             */
            findManyExportPorts: (query, params = {}) => this.request({
                path: `/base-data/export-ports`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name RemoveManyExportPorts
             * @summary 批量删除出口港
             * @request DELETE:/base-data/export-ports
             */
            removeManyExportPorts: (data, params = {}) => this.request({
                path: `/base-data/export-ports`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name FindAllExportPorts
             * @summary 查询所有出口港
             * @request GET:/base-data/export-ports/find/all
             */
            findAllExportPorts: (params = {}) => this.request({
                path: `/base-data/export-ports/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name FindOneExportPort
             * @summary 查询出口港详情
             * @request GET:/base-data/export-ports/{id}
             */
            findOneExportPort: (id, params = {}) => this.request({
                path: `/base-data/export-ports/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name UpdateExportPort
             * @summary 更新出口港
             * @request PATCH:/base-data/export-ports/{id}
             */
            updateExportPort: (id, data, params = {}) => this.request({
                path: `/base-data/export-ports/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口港管理
             * @name RemoveExportPort
             * @summary 删除出口港
             * @request DELETE:/base-data/export-ports/{id}
             */
            removeExportPort: (id, params = {}) => this.request({
                path: `/base-data/export-ports/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name CreateExportTradeMethod
             * @summary 创建贸易方式
             * @request POST:/base-data/export-trade-methods
             */
            createExportTradeMethod: (data, params = {}) => this.request({
                path: `/base-data/export-trade-methods`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name FindManyExportTradeMethods
             * @summary 分页查询贸易方式列表
             * @request GET:/base-data/export-trade-methods
             */
            findManyExportTradeMethods: (query, params = {}) => this.request({
                path: `/base-data/export-trade-methods`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name RemoveManyExportTradeMethods
             * @summary 批量删除贸易方式
             * @request DELETE:/base-data/export-trade-methods
             */
            removeManyExportTradeMethods: (data, params = {}) => this.request({
                path: `/base-data/export-trade-methods`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name FindAllExportTradeMethods
             * @summary 查询所有贸易方式
             * @request GET:/base-data/export-trade-methods/find/all
             */
            findAllExportTradeMethods: (params = {}) => this.request({
                path: `/base-data/export-trade-methods/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name FindOneExportTradeMethod
             * @summary 查询贸易方式详情
             * @request GET:/base-data/export-trade-methods/{id}
             */
            findOneExportTradeMethod: (id, params = {}) => this.request({
                path: `/base-data/export-trade-methods/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name UpdateExportTradeMethod
             * @summary 更新贸易方式
             * @request PATCH:/base-data/export-trade-methods/{id}
             */
            updateExportTradeMethod: (id, data, params = {}) => this.request({
                path: `/base-data/export-trade-methods/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 贸易方式管理
             * @name RemoveExportTradeMethod
             * @summary 删除贸易方式
             * @request DELETE:/base-data/export-trade-methods/{id}
             */
            removeExportTradeMethod: (id, params = {}) => this.request({
                path: `/base-data/export-trade-methods/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name CreatePaymentItem
             * @summary 创建款项
             * @request POST:/base-data/payment-items
             */
            createPaymentItem: (data, params = {}) => this.request({
                path: `/base-data/payment-items`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name FindManyPaymentItems
             * @summary 分页查询款项列表
             * @request GET:/base-data/payment-items
             */
            findManyPaymentItems: (query, params = {}) => this.request({
                path: `/base-data/payment-items`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name RemoveManyPaymentItems
             * @summary 批量删除款项
             * @request DELETE:/base-data/payment-items
             */
            removeManyPaymentItems: (data, params = {}) => this.request({
                path: `/base-data/payment-items`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name FindAllPaymentItems
             * @summary 查询所有款项
             * @request GET:/base-data/payment-items/find/all
             */
            findAllPaymentItems: (params = {}) => this.request({
                path: `/base-data/payment-items/find/all`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name FindOnePaymentItem
             * @summary 查询款项详情
             * @request GET:/base-data/payment-items/{id}
             */
            findOnePaymentItem: (id, params = {}) => this.request({
                path: `/base-data/payment-items/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name UpdatePaymentItem
             * @summary 更新款项
             * @request PATCH:/base-data/payment-items/{id}
             */
            updatePaymentItem: (id, data, params = {}) => this.request({
                path: `/base-data/payment-items/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 款项管理
             * @name RemovePaymentItem
             * @summary 删除款项
             * @request DELETE:/base-data/payment-items/{id}
             */
            removePaymentItem: (id, params = {}) => this.request({
                path: `/base-data/payment-items/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 每日汇率
             * @name FindMany
             * @summary 分页查询每日汇率列表
             * @request GET:/base-data/daily-exchange-rates
             */
            findMany: (query, params = {}) => this.request({
                path: `/base-data/daily-exchange-rates`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 每日汇率
             * @name FindTodayRate
             * @summary 查询今日指定币种汇率
             * @request GET:/base-data/daily-exchange-rates/today-rate
             */
            findTodayRate: (query, params = {}) => this.request({
                path: `/base-data/daily-exchange-rates/today-rate`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 每日汇率
             * @name RefreshToday
             * @summary 更新今日汇率
             * @request POST:/base-data/daily-exchange-rates/refresh-today
             */
            refreshToday: (data, params = {}) => this.request({
                path: `/base-data/daily-exchange-rates/refresh-today`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
        };
        this.logistics = {
            /**
             * No description
             *
             * @tags 物流
             * @name Create
             * @summary 创建物流（含车辆明细和附件）
             * @request POST:/logistics
             */
            create: (data, params = {}) => this.request({
                path: `/logistics`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name FindMany
             * @summary 分页查询物流列表
             * @request GET:/logistics
             */
            findMany: (query, params = {}) => this.request({
                path: `/logistics`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name RemoveMany
             * @summary 批量删除物流
             * @request DELETE:/logistics
             */
            removeMany: (data, params = {}) => this.request({
                path: `/logistics`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name FindFollowUp
             * @summary 查询物流跟进概览
             * @request GET:/logistics/follow-up
             */
            findFollowUp: (query, params = {}) => this.request({
                path: `/logistics/follow-up`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name FindOne
             * @summary 查询物流详情
             * @request GET:/logistics/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/logistics/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name Update
             * @summary 更新物流（含车辆明细和附件）
             * @request PATCH:/logistics/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/logistics/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 物流
             * @name Remove
             * @summary 删除物流
             * @request DELETE:/logistics/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/logistics/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.inventoryStocktaking = {
            /**
             * No description
             *
             * @tags 库存盘点
             * @name FindOverview
             * @summary 查询库存盘点概览
             * @request GET:/inventory-stocktaking/overview
             */
            findOverview: (params = {}) => this.request({
                path: `/inventory-stocktaking/overview`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 库存盘点
             * @name FindVehicles
             * @summary 分页查询库存盘点车辆明细
             * @request GET:/inventory-stocktaking/vehicles
             */
            findVehicles: (query, params = {}) => this.request({
                path: `/inventory-stocktaking/vehicles`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
        };
        this.exportLicense = {
            /**
             * No description
             *
             * @tags 出口许可证
             * @name Create
             * @summary 创建出口许可证（含车辆明细和单附件）
             * @request POST:/export-license
             */
            create: (data, params = {}) => this.request({
                path: `/export-license`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口许可证
             * @name FindMany
             * @summary 分页查询出口许可证 VIN 维度列表
             * @request GET:/export-license
             */
            findMany: (query, params = {}) => this.request({
                path: `/export-license`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口许可证
             * @name RemoveMany
             * @summary 批量删除出口许可证
             * @request DELETE:/export-license
             */
            removeMany: (data, params = {}) => this.request({
                path: `/export-license`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口许可证
             * @name FindOne
             * @summary 查询出口许可证详情
             * @request GET:/export-license/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/export-license/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口许可证
             * @name Update
             * @summary 更新出口许可证（含车辆明细和单附件）
             * @request PATCH:/export-license/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/export-license/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 出口许可证
             * @name Remove
             * @summary 删除出口许可证
             * @request DELETE:/export-license/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/export-license/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.customsDeclaration = {
            /**
             * No description
             *
             * @tags 报关
             * @name Create
             * @summary 创建报关单
             * @request POST:/customs-declaration
             */
            create: (data, params = {}) => this.request({
                path: `/customs-declaration`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 报关
             * @name FindMany
             * @summary 分页查询报关 VIN 维度列表
             * @request GET:/customs-declaration
             */
            findMany: (query, params = {}) => this.request({
                path: `/customs-declaration`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 报关
             * @name RemoveMany
             * @summary 批量删除报关单
             * @request DELETE:/customs-declaration
             */
            removeMany: (data, params = {}) => this.request({
                path: `/customs-declaration`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 报关
             * @name FindOne
             * @summary 查询报关单详情
             * @request GET:/customs-declaration/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/customs-declaration/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 报关
             * @name Update
             * @summary 更新报关单
             * @request PATCH:/customs-declaration/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/customs-declaration/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 报关
             * @name Remove
             * @summary 删除报关单
             * @request DELETE:/customs-declaration/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/customs-declaration/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.vehicleDelivery = {
            /**
             * No description
             *
             * @tags 车辆交付
             * @name Create
             * @summary 创建车辆交付单
             * @request POST:/vehicle-delivery
             */
            create: (data, params = {}) => this.request({
                path: `/vehicle-delivery`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆交付
             * @name FindMany
             * @summary 分页查询车辆交付 VIN 维度列表
             * @request GET:/vehicle-delivery
             */
            findMany: (query, params = {}) => this.request({
                path: `/vehicle-delivery`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆交付
             * @name RemoveMany
             * @summary 批量删除车辆交付单
             * @request DELETE:/vehicle-delivery
             */
            removeMany: (data, params = {}) => this.request({
                path: `/vehicle-delivery`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆交付
             * @name FindOne
             * @summary 查询车辆交付单详情
             * @request GET:/vehicle-delivery/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/vehicle-delivery/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆交付
             * @name Update
             * @summary 更新车辆交付单
             * @request PATCH:/vehicle-delivery/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/vehicle-delivery/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆交付
             * @name Remove
             * @summary 删除车辆交付单
             * @request DELETE:/vehicle-delivery/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/vehicle-delivery/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.vehicleInbound = {
            /**
             * No description
             *
             * @tags 车辆入库
             * @name Create
             * @summary 创建车辆入库单并发起确认流程
             * @request POST:/vehicle-inbound
             */
            create: (data, params = {}) => this.request({
                path: `/vehicle-inbound`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name FindMany
             * @summary 分页查询车辆入库单
             * @request GET:/vehicle-inbound
             */
            findMany: (query, params = {}) => this.request({
                path: `/vehicle-inbound`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name FindOne
             * @summary 查询车辆入库单详情
             * @request GET:/vehicle-inbound/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/vehicle-inbound/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name Update
             * @summary 修改车辆入库单
             * @request PATCH:/vehicle-inbound/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/vehicle-inbound/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name Remove
             * @summary 删除车辆入库单
             * @request DELETE:/vehicle-inbound/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/vehicle-inbound/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name FindWorkflowTimeline
             * @summary 查询车辆入库流程日志
             * @request GET:/vehicle-inbound/{id}/workflow-timeline
             */
            findWorkflowTimeline: (id, params = {}) => this.request({
                path: `/vehicle-inbound/${id}/workflow-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆入库
             * @name FindOperationTimeline
             * @summary 查询车辆入库操作历史
             * @request GET:/vehicle-inbound/{id}/operation-timeline
             */
            findOperationTimeline: (id, params = {}) => this.request({
                path: `/vehicle-inbound/${id}/operation-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.vehicleOutbound = {
            /**
             * No description
             *
             * @tags 车辆出库
             * @name Create
             * @summary 创建车辆出库单并发起审批流程
             * @request POST:/vehicle-outbound
             */
            create: (data, params = {}) => this.request({
                path: `/vehicle-outbound`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name FindMany
             * @summary 分页查询车辆出库单
             * @request GET:/vehicle-outbound
             */
            findMany: (query, params = {}) => this.request({
                path: `/vehicle-outbound`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name FindAvailableVehicles
             * @summary 分页查询可出库的在库车辆
             * @request GET:/vehicle-outbound/available-vehicles
             */
            findAvailableVehicles: (query, params = {}) => this.request({
                path: `/vehicle-outbound/available-vehicles`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name FindOne
             * @summary 查询车辆出库单详情
             * @request GET:/vehicle-outbound/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/vehicle-outbound/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name Update
             * @summary 修改车辆出库单
             * @request PATCH:/vehicle-outbound/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/vehicle-outbound/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name Remove
             * @summary 删除车辆出库单
             * @request DELETE:/vehicle-outbound/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/vehicle-outbound/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name FindWorkflowTimeline
             * @summary 查询车辆出库流程日志
             * @request GET:/vehicle-outbound/{id}/workflow-timeline
             */
            findWorkflowTimeline: (id, params = {}) => this.request({
                path: `/vehicle-outbound/${id}/workflow-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆出库
             * @name FindOperationTimeline
             * @summary 查询车辆出库操作历史
             * @request GET:/vehicle-outbound/{id}/operation-timeline
             */
            findOperationTimeline: (id, params = {}) => this.request({
                path: `/vehicle-outbound/${id}/operation-timeline`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
        };
        this.vehicleCancellation = {
            /**
             * No description
             *
             * @tags 车辆注销
             * @name Create
             * @summary 创建车辆注销单
             * @request POST:/vehicle-cancellation
             */
            create: (data, params = {}) => this.request({
                path: `/vehicle-cancellation`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆注销
             * @name FindMany
             * @summary 分页查询车辆注销 VIN 维度列表
             * @request GET:/vehicle-cancellation
             */
            findMany: (query, params = {}) => this.request({
                path: `/vehicle-cancellation`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆注销
             * @name RemoveMany
             * @summary 批量删除车辆注销单
             * @request DELETE:/vehicle-cancellation
             */
            removeMany: (data, params = {}) => this.request({
                path: `/vehicle-cancellation`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆注销
             * @name FindOne
             * @summary 查询车辆注销单详情
             * @request GET:/vehicle-cancellation/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/vehicle-cancellation/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆注销
             * @name Update
             * @summary 更新车辆注销单
             * @request PATCH:/vehicle-cancellation/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/vehicle-cancellation/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆注销
             * @name Remove
             * @summary 删除车辆注销单
             * @request DELETE:/vehicle-cancellation/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/vehicle-cancellation/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
        this.paymentRequest = {
            /**
             * No description
             *
             * @tags 付款申请
             * @name Create
             * @summary 创建付款申请
             * @request POST:/payment-request
             */
            create: (data, params = {}) => this.request({
                path: `/payment-request`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 付款申请
             * @name FindMany
             * @summary 分页查询付款申请
             * @request GET:/payment-request
             */
            findMany: (query, params = {}) => this.request({
                path: `/payment-request`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 付款申请
             * @name FindOne
             * @summary 查询付款申请详情
             * @request GET:/payment-request/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/payment-request/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 付款申请
             * @name Update
             * @summary 修改付款申请
             * @request PATCH:/payment-request/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/payment-request/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 付款申请
             * @name Remove
             * @summary 删除付款申请
             * @request DELETE:/payment-request/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/payment-request/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 付款申请
             * @name Submit
             * @summary 提交已保存的付款申请
             * @request POST:/payment-request/{id}/submit
             */
            submit: (id, params = {}) => this.request({
                path: `/payment-request/${id}/submit`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
        };
        this.receiptRequest = {
            /**
             * No description
             *
             * @tags 收款申请
             * @name Create
             * @summary 创建收款申请
             * @request POST:/receipt-request
             */
            create: (data, params = {}) => this.request({
                path: `/receipt-request`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 收款申请
             * @name FindMany
             * @summary 分页查询收款申请
             * @request GET:/receipt-request
             */
            findMany: (query, params = {}) => this.request({
                path: `/receipt-request`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 收款申请
             * @name FindOne
             * @summary 查询收款申请详情
             * @request GET:/receipt-request/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/receipt-request/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 收款申请
             * @name Update
             * @summary 修改收款申请
             * @request PATCH:/receipt-request/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/receipt-request/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 收款申请
             * @name Remove
             * @summary 删除收款申请
             * @request DELETE:/receipt-request/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/receipt-request/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 收款申请
             * @name Submit
             * @summary 提交已保存的收款申请
             * @request POST:/receipt-request/{id}/submit
             */
            submit: (id, params = {}) => this.request({
                path: `/receipt-request/${id}/submit`,
                method: 'POST',
                format: 'json',
                ...params,
            }),
        };
        this.taxRefund = {
            /**
             * No description
             *
             * @tags 车辆退税
             * @name Create
             * @summary 创建车辆退税单
             * @request POST:/tax-refund
             */
            create: (data, params = {}) => this.request({
                path: `/tax-refund`,
                method: 'POST',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆退税
             * @name FindMany
             * @summary 分页查询车辆退税 VIN 维度列表
             * @request GET:/tax-refund
             */
            findMany: (query, params = {}) => this.request({
                path: `/tax-refund`,
                method: 'GET',
                query: query,
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆退税
             * @name RemoveMany
             * @summary 批量删除未发生到账的车辆退税单
             * @request DELETE:/tax-refund
             */
            removeMany: (data, params = {}) => this.request({
                path: `/tax-refund`,
                method: 'DELETE',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆退税
             * @name FindOne
             * @summary 查询车辆退税单详情
             * @request GET:/tax-refund/{id}
             */
            findOne: (id, params = {}) => this.request({
                path: `/tax-refund/${id}`,
                method: 'GET',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆退税
             * @name Update
             * @summary 更新车辆退税单或新增退税到账批次
             * @request PATCH:/tax-refund/{id}
             */
            update: (id, data, params = {}) => this.request({
                path: `/tax-refund/${id}`,
                method: 'PATCH',
                body: data,
                type: 'application/json',
                format: 'json',
                ...params,
            }),
            /**
             * No description
             *
             * @tags 车辆退税
             * @name Remove
             * @summary 删除未发生到账的车辆退税单
             * @request DELETE:/tax-refund/{id}
             */
            remove: (id, params = {}) => this.request({
                path: `/tax-refund/${id}`,
                method: 'DELETE',
                format: 'json',
                ...params,
            }),
        };
    }
}
//# sourceMappingURL=index.js.map